import type {
  ApiErrorBody,
  CustomFieldDefinition,
  ErrorCode,
  PullResponse,
  PushResponse,
} from '@clim/domain';
import { ERROR_CODES } from '@clim/domain';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'NETWORK',
    message: string,
    readonly details?: unknown,
    readonly requestId?: string,
  ) {
    super(message);
  }
  get isNetwork() {
    return this.code === 'NETWORK';
  }
}

/** Where the refresh token lives: OS keychain on mobile; nowhere on web (httpOnly cookie). */
export interface TokenStore {
  getRefreshToken(): Promise<string | null>;
  setRefreshToken(token: string | null): Promise<void>;
}

export interface ApiClientOptions {
  baseUrl: string;
  mode: 'web' | 'mobile';
  tokenStore?: TokenStore;
  deviceId: string;
  fetch?: typeof fetch;
  onAuthLost?: () => void;
}

export interface LoginInput {
  tenant: string;
  email: string;
  password: string;
  deviceName?: string;
  platform: 'web' | 'ios' | 'android' | 'other';
}

export interface Me {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phone: string | null;
    mustChangePassword: boolean;
    isContractor?: boolean;
    mfaEnabled?: boolean;
    mfaSetupRequired?: boolean;
  };
  tenant: {
    id: string;
    slug: string;
    name: string;
    settings: {
      legal: Record<string, string>;
      branding: { logoUrl?: string; primaryColor?: string };
      clientCustomFields: CustomFieldDefinition[];
      /** champs facultatifs du dossier commercial (absent : serveur plus ancien → masqués) */
      dossierForm?: { showSaleChannel: boolean; showProActivity: boolean };
      /** bons d'enlèvement : vérification par le fournisseur (absent = désactivée) */
      pickupOrders?: { supplierVerification: boolean };
    };
  };
  roles: string[];
  permissions: Record<string, 'own' | 'territory' | 'all'>;
  territoryIds: string[];
}

/**
 * HTTP client shared by web and mobile. The access token only lives in memory; a 401 triggers
 * ONE refresh (single-flight, concurrent callers wait for it) and the request is retried once.
 */
export interface UploadState {
  receivedBytes: number;
  sizeBytes: number;
  status: 'pending' | 'ready';
  chunkBytes: number;
}

export class ApiClient {
  private accessToken: string | null = null;
  private refreshing: Promise<boolean> | null = null;
  /** dernier renouvellement en échec pour une raison passagère (réseau, serveur indisponible) */
  private refreshUnavailable = false;
  private readonly f: typeof fetch;

  constructor(private readonly opts: ApiClientOptions) {
    this.f = opts.fetch ?? globalThis.fetch.bind(globalThis);
  }

  get isAuthenticated() {
    return this.accessToken !== null;
  }

  /**
   * Password step. Returns `{ mfaToken }` when the account has two-step verification: the session opens only
   * with `loginMfa` (code from the authenticator app, or a recovery code).
   */
  async login(input: LoginInput): Promise<{ mfaToken: string } | null> {
    const res = (await this.raw(
      'POST',
      '/v1/auth/login',
      { ...input, deviceId: this.opts.deviceId },
      false,
    )) as { accessToken: string; refreshToken?: string } | { mfaRequired: true; mfaToken: string };
    if ('mfaRequired' in res) return { mfaToken: res.mfaToken };
    await this.acceptTokens(res);
    return null;
  }

  async loginMfa(mfaToken: string, code: string): Promise<void> {
    const res = await this.raw('POST', '/v1/auth/login/mfa', { mfaToken, code: code.trim() }, false);
    await this.acceptTokens(res as { accessToken: string; refreshToken?: string });
  }

  /** Restores a session at app start (refresh token from keychain / cookie). */
  async resume(): Promise<boolean> {
    return this.refresh();
  }

  async logout(): Promise<void> {
    try {
      await this.request('POST', '/v1/auth/logout', {});
    } catch {
      // offline logout: local credentials are dropped anyway
    }
    this.accessToken = null;
    await this.opts.tokenStore?.setRefreshToken(null);
  }

  me() {
    return this.request<Me>('GET', '/v1/auth/me');
  }

  push(body: { deviceId: string; mutations: unknown[] }) {
    return this.request<PushResponse>('POST', '/v1/sync/push', body);
  }

  pull(cursor: string, limit = 500) {
    const q = new URLSearchParams({ limit: String(limit) });
    if (cursor) q.set('cursor', cursor);
    return this.request<PullResponse>('GET', `/v1/sync/pull?${q}`);
  }

  /* ---------------------------------------------------------------- files */

  uploadStatus(fileId: string) {
    return this.request<UploadState>('GET', `/v1/files/${fileId}/upload`);
  }
  uploadChunk(fileId: string, offset: number, bytes: Uint8Array) {
    return this.request<UploadState>('PUT', `/v1/files/${fileId}/content?offset=${offset}`, bytes);
  }
  /** absolute URL for a path returned by the API (signed file links…) */
  resolveUrl(url: string): string {
    return url.startsWith('/') ? `${this.opts.baseUrl}${url}` : url;
  }

  /** temporary URL (absolute) to display / download a private file */
  async fileUrl(fileId: string, download = false): Promise<string> {
    const r = await this.request<{ url: string }>(
      'GET',
      `/v1/files/${fileId}/url${download ? '?download=1' : ''}`,
    );
    return r.url.startsWith('/') ? `${this.opts.baseUrl}${r.url}` : r.url;
  }

  async request<T = unknown>(method: string, path: string, body?: unknown): Promise<T> {
    if (!this.accessToken) await this.refresh();
    try {
      return (await this.raw(method, path, body, true)) as T;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401 && err.code === 'UNAUTHENTICATED') {
        if (await this.refresh()) return (await this.raw(method, path, body, true)) as T;
        // serveur momentanément indisponible : on réessaiera, la session n'est pas perdue
        if (this.refreshUnavailable) throw new ApiError(0, 'NETWORK', 'Serveur momentanément indisponible');
        this.opts.onAuthLost?.();
      }
      throw err;
    }
  }

  private refresh(): Promise<boolean> {
    this.refreshing ??= (async () => {
      this.refreshUnavailable = false;
      try {
        const payload =
          this.opts.mode === 'mobile' ? { refreshToken: await this.opts.tokenStore?.getRefreshToken() } : {};
        if (this.opts.mode === 'mobile' && !payload.refreshToken) return false;
        const res = await this.raw('POST', '/v1/auth/refresh', payload, false);
        await this.acceptTokens(res as { accessToken: string; refreshToken?: string });
        return true;
      } catch (err) {
        // seul un refus explicite (401) ferme la session : une erreur du serveur (redémarrage pendant une mise à
        // jour, 502…) ou du réseau garde le jeton pour réessayer plus tard
        if (err instanceof ApiError && err.status === 401) {
          this.accessToken = null;
          await this.opts.tokenStore?.setRefreshToken(null);
        } else this.refreshUnavailable = true;
        return false;
      } finally {
        this.refreshing = null;
      }
    })();
    return this.refreshing;
  }

  private async acceptTokens(res: { accessToken: string; refreshToken?: string }) {
    this.accessToken = res.accessToken;
    if (this.opts.mode === 'mobile' && res.refreshToken)
      await this.opts.tokenStore?.setRefreshToken(res.refreshToken);
  }

  private async raw(method: string, path: string, body: unknown, withAuth: boolean): Promise<unknown> {
    const headers: Record<string, string> = { 'x-device-id': this.opts.deviceId };
    const binary = body instanceof Uint8Array;
    if (body !== undefined)
      headers['content-type'] = binary ? 'application/octet-stream' : 'application/json';
    if (this.opts.mode === 'web') headers['x-client'] = 'web';
    if (withAuth && this.accessToken) headers.authorization = `Bearer ${this.accessToken}`;
    let res: Response;
    try {
      res = await this.f(`${this.opts.baseUrl}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : binary ? (body as BodyInit) : JSON.stringify(body),
        credentials: this.opts.mode === 'web' ? 'include' : 'omit',
      });
    } catch (err) {
      throw new ApiError(0, 'NETWORK', 'Hors ligne ou serveur injoignable', String(err));
    }
    if (res.status === 204) return null;
    // binary downloads (PDF statements…) are returned as bytes
    if (res.ok && (res.headers?.get('content-type') ?? '').includes('application/pdf'))
      return new Uint8Array(await res.arrayBuffer());
    const text = await res.text();
    // non-JSON success bodies (CSV export, …) are returned as text
    const type = res.headers?.get('content-type') ?? '';
    if (res.ok && type && !type.includes('json')) return text;
    let json: unknown = null;
    try {
      json = text ? (JSON.parse(text) as unknown) : null;
    } catch {
      // page d'erreur du serveur web (502 pendant une mise à jour…) : pas du JSON
      if (res.ok) throw new ApiError(res.status, 'INTERNAL', 'Réponse inattendue du serveur');
    }
    if (!res.ok) {
      const e = (json as ApiErrorBody | null)?.error;
      const code = (e?.code ?? 'INTERNAL') as ErrorCode;
      throw new ApiError(
        res.status,
        code,
        e?.message ?? ERROR_CODES[code] ?? 'Erreur',
        e?.details,
        (json as ApiErrorBody | null)?.requestId,
      );
    }
    return json;
  }
}
